import { Component } from '@theme/component';

/**
 * `<bundle-reviews data-widget="badge">` — the star rating + review count
 * shown under each step's title, for the PRODUCT BEING VIEWED.
 *
 * The rating and count ship inline on the builder config (per product), so
 * this renders synchronously on every `bundle:update` — no network, no
 * debounce — and updates the instant the type / colour / step changes.
 *
 * The full Judge.me review list lives in `<bundle-reviews-widget>`
 * (assets/bb-reviews-widget.js), which this component used to also drive by
 * fetching `/products/{handle}` HTML; that path is gone.
 *
 * Owns ONLY `<bundle-reviews>`. Refs: widget.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} widget
 * @extends Component<Refs>
 */
export class BundleReviewsComponent extends Component {
  requiredRefs = ['widget'];

  /** productId currently rendered, to skip redundant re-renders. */
  #currentId = '';

  connectedCallback() {
    super.connectedCallback();
    this.#builder?.addEventListener('bundle:update', this.#onUpdate);
    this.#builder?.requestUpdate?.();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#builder?.removeEventListener('bundle:update', this.#onUpdate);
  }

  /** @returns {Element | null} */
  get #builder() {
    return this.closest('bundle-builder') || document.querySelector('bundle-builder');
  }

  #onUpdate = () => {
    const builder = this.#builder;
    // Follow the PRODUCT BEING VIEWED (draft selection of the current step),
    // not the committed bundle — the badge rates the item named above it.
    // @ts-ignore — getDraftSelection is defined on BundleBuilderComponent.
    const sel = builder?.getDraftSelection?.(builder.state?.currentStep);
    const product = sel?.product;
    if (!product) return;

    const key = String(product.productId ?? '');
    if (key && key === this.#currentId) return;
    this.#currentId = key;

    const { widget } = this.refs;
    if (!widget) return;
    widget.innerHTML = this.#renderStars(Number(product.rating) || 0, Number(product.reviewCount) || 0);
  };

  /**
   * Custom star rating + review count (gold stars filled to the average).
   * @param {number} rating
   * @param {number} count
   */
  #renderStars(rating, count) {
    const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
    return (
      `<span class="bb-stars" role="img" aria-label="${rating.toFixed(1)} out of 5 stars" style="--bb-star-pct:${pct}%"></span>` +
      `<span class="bb-stars__count">${count} review${count === 1 ? '' : 's'}</span>`
    );
  }
}

if (!customElements.get('bundle-reviews')) {
  customElements.define('bundle-reviews', BundleReviewsComponent);
}
